import { useRef, useState } from 'react';
import adminPanelIcon from '../../assets/img/ufo.png';
import useLoading from '../../hooks/useLoading';
import AuthController from '../../Api/authController';
import { useToast } from '../../hooks/useToast';
import { useUser } from '../../hooks/useUser';
import {
    Box,
    Container,
    Paper,
    Stack,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const buttonGradient =
    'linear-gradient(180deg, #2f3ea4 0%, #293a98 40%, #26358f 70%, #223083 100%)';

const SignIn = () => {
    const [username, setUsername] = useState('');
    const [passwordValue, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const loading = useLoading();
    const toast = useRef(useToast()).current;
    const user = useUser();

    const changePasswordHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    };

    const signIn = async () => {
        if (submittingRef.current) return;
        const pwd = passwordValue;
        if (!username.trim() || !pwd) {
            toast.error('Podaj login lub e-mail i hasło');
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        loading.show();
        try {
            const login = { username: username.trim(), password: pwd };
            const res = await AuthController.login(login);

            if (!res?.ok || !res?.token) {
                toast.error(res?.message || 'Logowanie nie powiodło się');
                return;
            }

            if (res.user) user.setSession(res.user);
            const permissions = res.user?.permissions || [];
            const all = res.user?.role?.toLowerCase() === 'superadmin' || permissions.includes('*');
            const has = (key: string) => all || permissions.includes(key) || permissions.includes(`${key.split('.')[0]}.*`);
            const destination = res.user?.mustChangePassword ? '/zmien-haslo'
                : has('admin.access') ? '/adminPanel'
                : has('users.read') ? '/admin/uzytkownicy'
                : has('invoices.read') ? '/proformy'
                : has('deliveryNotices.read') ? '/awizacje'
                : has('timeTracking.read') ? '/skanowanieObecnosci'
                : has('employees.read') ? '/pracownicy'
                : '/';
            window.location.href = destination;
        } catch {
            toast.error('Błąd połączenia. Spróbuj ponownie.');
        } finally {
            submittingRef.current = false;
            loading.hide();
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'grid',
                placeItems: 'center',
                py: { xs: 6, md: 8 },
                px: 2,
                position: 'relative',
                overflow: 'hidden',
                background:
                    'radial-gradient(1200px 600px at 80% -100px, rgba(20,136,204,0.10), transparent 60%), radial-gradient(900px 400px at -100px 120%, rgba(123,67,151,0.10), transparent 60%), linear-gradient(180deg, #0b102b 0%, #0e1233 100%)',
            }}
        >
            {/* Dekoracyjne gradientowe „blob-y” */}
            <Box
                sx={{
                    position: 'absolute',
                    width: '60vw',
                    height: '60vw',
                    top: '-25%',
                    right: '-20%',
                    opacity: 0.25,
                    filter: 'blur(28px)',
                    pointerEvents: 'none',
                    background:
                        'radial-gradient(50% 50% at 50% 50%, rgba(47,62,164,0.9) 0%, rgba(47,62,164,0.0) 60%)',
                    '@keyframes float1': {
                        '0%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-18px)' },
                        '100%': { transform: 'translateY(0px)' },
                    },
                    animation: 'float1 16s ease-in-out infinite',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    width: '55vw',
                    height: '55vw',
                    bottom: '-25%',
                    left: '-20%',
                    opacity: 0.22,
                    filter: 'blur(30px)',
                    pointerEvents: 'none',
                    background:
                        'radial-gradient(50% 50% at 50% 50%, rgba(34,48,131,0.9) 0%, rgba(34,48,131,0.0) 60%)',
                    '@keyframes float2': {
                        '0%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(14px)' },
                        '100%': { transform: 'translateY(0px)' },
                    },
                    animation: 'float2 20s ease-in-out infinite',
                }}
            />

            <Container maxWidth="xs" sx={{ position: 'relative' }}>
                {/* Neonowa obręcz za kartą */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: { xs: -10, sm: -14 },
                        borderRadius: 4,
                        pointerEvents: 'none',
                        opacity: 0.45,
                        filter: 'blur(24px)',
                        background:
                            'conic-gradient(from 0deg, #2f3ea4, #223083, #6b73ff, #2f3ea4)',
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg) scale(1.02)' },
                            '100%': { transform: 'rotate(360deg) scale(1.02)' },
                        },
                        animation: 'spin 28s linear infinite',
                    }}
                />

                {/* Gradientowa ramka */}
                <Box
                    sx={{
                        p: 0.8,
                        borderRadius: 3.5,
                        position: 'relative',
                        background:
                            'linear-gradient(120deg, rgba(47,62,164,0.8), rgba(34,48,131,0.8))',
                    }}
                >
                    <Paper
                        component="form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            signIn();
                        }}
                        elevation={16}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 3,
                            backdropFilter: 'blur(10px)',
                            background:
                                'linear-gradient(180deg, rgba(16,20,50,0.86), rgba(16,20,50,0.92))',
                            border: '1px solid rgba(255,255,255,0.06)',
                            boxShadow:
                                '0 10px 24px rgba(20,20,40,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
                            transition: 'transform .25s ease, box-shadow .25s ease',
                            color: 'rgba(231,235,255,0.92)', // bazowy jasny kolor tekstu
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow:
                                    '0 14px 34px rgba(20,20,40,0.45), inset 0 1px 0 rgba(255,255,255,0.05)',
                            },
                        }}
                    >
                        <Stack spacing={3.5} alignItems="center">
                            <Box
                                onClick={() => (window.location.href = '/')}
                                sx={{
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    position: 'relative',
                                }}
                            >
                                {/* delikatna poświata pod logo */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: -10,
                                        filter: 'blur(14px)',
                                        borderRadius: '50%',
                                        background:
                                            'radial-gradient(50% 50% at 50% 50%, rgba(47,62,164,0.45), rgba(34,48,131,0.0))',
                                    }}
                                />
                                <Box
                                    component="img"
                                    src={adminPanelIcon}
                                    alt="ufo logo"
                                    sx={{ width: 96, height: 96, objectFit: 'contain', position: 'relative' }}
                                />
                            </Box>

                            <Stack spacing={0.5} alignItems="center">
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: '-.01em',
                                        color: '#e8ebff',
                                        textShadow: '0 1px 0 rgba(0,0,0,0.3)',
                                    }}
                                >
                                    Logowanie
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'rgba(231,235,255,0.7)' }}
                                >
                                    Dostęp do panelu administracyjnego
                                </Typography>
                            </Stack>

                            <Box sx={{ alignSelf: 'stretch', width: '100%' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(231,235,255,0.75)' }}>Login lub e-mail</Typography>
                                <TextField
                                    fullWidth
                                    value={username}
                                    onChange={(event) => setUsername(event.target.value)}
                                    autoComplete="username"
                                    placeholder="Wpisz login lub e-mail"
                                    autoFocus
                                    InputProps={{ sx: { color: '#e8ebff', background: 'rgba(255,255,255,0.04)', borderRadius: 2 } }}
                                    sx={{ mb: 2 }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, color: 'rgba(231,235,255,0.75)' }}
                                >
                                    Hasło
                                </Typography>

                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="current-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={passwordValue}
                                    onChange={changePasswordHandler}
                                    placeholder="Wpisz hasło"
                                    InputLabelProps={{
                                        sx: {
                                            color: 'rgba(231,235,255,0.75)',
                                            '&.Mui-focused': { color: '#e8ebff' },
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                                                    onClick={() => setShowPassword((v) => !v)}
                                                    edge="end"
                                                    size="small"
                                                    sx={{ color: 'rgba(231,235,255,0.85)' }}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            color: '#e8ebff',            // kolor tekstu w polu
                                            caretColor: '#fff',
                                            background: 'rgba(255,255,255,0.04)',
                                            borderRadius: 2,
                                            '& input::placeholder': {
                                                color: 'rgba(231,235,255,0.55)',
                                                opacity: 1,
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(231,235,255,0.18)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(231,235,255,0.28)',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#6b73ff',
                                                boxShadow: '0 0 0 3px rgba(107,115,255,0.15)',
                                            },
                                            '& input:-webkit-autofill': {
                                                WebkitBoxShadow: '0 0 0 1000px rgba(255,255,255,0.06) inset',
                                                WebkitTextFillColor: '#e8ebff',
                                                caretColor: '#fff',
                                                transition: 'background-color 5000s ease-in-out 0s',
                                                borderRadius: 8,
                                            },
                                        },
                                    }}
                                />
                                <Button href="/odzyskaj-haslo" sx={{ mt: 1, color: 'rgba(231,235,255,0.8)' }}>Nie pamiętam hasła</Button>
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                size="large"
                                disabled={isSubmitting}
                                onClick={signIn}
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    py: 1.25,
                                    borderRadius: 999,
                                    color: '#fff',
                                    backgroundImage: buttonGradient,
                                    textTransform: 'uppercase',
                                    fontWeight: 800,
                                    letterSpacing: '.08em',
                                    boxShadow: '0 10px 26px rgba(34,48,131,0.35)',
                                    transition: 'transform .15s ease, box-shadow .2s ease, filter .2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        filter: 'brightness(1.02)',
                                        boxShadow: '0 12px 30px rgba(34,48,131,0.45)',
                                    },
                                    '&:disabled': {
                                        opacity: 0.7,
                                        boxShadow: 'none',
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-120%',
                                        width: '80px',
                                        height: '100%',
                                        background:
                                            'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
                                        transform: 'skewX(-20deg)',
                                        transition: 'left .7s ease',
                                    },
                                    '&:hover::before': {
                                        left: '130%',
                                    },
                                }}
                            >
                                {isSubmitting ? (
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <CircularProgress size={20} thickness={5} sx={{ color: '#fff' }} />
                                        <span>Logowanie…</span>
                                    </Stack>
                                ) : (
                                    'Zaloguj'
                                )}
                            </Button>

                            <Typography variant="caption" sx={{ color: 'rgba(231,235,255,0.7)' }}>
                                Naciśnij Enter, aby zatwierdzić
                            </Typography>
                        </Stack>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default SignIn;
// import { useRef, useState } from 'react';
// import styles from './signIn.module.scss';
// import adminPanelIcon from '../../assets/img/ufo.png';
// import useLoading from '../../hooks/useLoading';
// import AuthController from '../../Api/authController';
// import { useToast } from '../../hooks/useToast';
// import { useUser } from '../../hooks/useUser';

// const SignIn = () => {
//     const [passwordValue, setPassword] = useState('');
//     const loading = useLoading();
//     const toast = useRef(useToast()).current;
//     const user = useUser()

//     const changePasswordHandler = (event: any) => {
//         const password = event.target.value;
//         setPassword(password);
//     }


//     const signIn = async () => {
//         loading.show();
//         let login;
//         const showroom = localStorage.getItem('selectedShowroom');
//         login = {
//             username: 'minsk',
//             password: passwordValue
//         };
//         if (!login) return;
//         const res = await AuthController.login(login)
//         if (!res.ok || !res.token) {
//             toast.error(res.message);
//             loading.hide();
//             return
//         }
//         if (showroom === 'minsk') {
//             localStorage.setItem('fakeToken', res.token)
//             user.setIsAuth(true);
//             window.location.href = '/adminPanel';
//         }
//         loading.hide();
//     }

//     return (
//         <div className={styles.general}>
//             <div className={styles.generalContainer}>
//                 <div className={styles.loginContainer}>
//                     <div className={styles.imageContainer}>
//                         <img onClick={() => window.location.href = '/'} className={styles.logo} alt='ufo logo' src={adminPanelIcon} />
//                     </div>
//                     <div className={styles.captionLogin}>Logowanie</div>
//                     <div className={styles.captionPassword}>
//                         Hasło
//                     </div>
//                     <div className={styles.passwordInputContainer}>
//                         <input className={styles.passwordInput}
//                             onKeyDown={e => { if (e.key === 'Enter') signIn() }}
//                             onChange={changePasswordHandler}
//                             value={passwordValue}
//                             type='password' />
//                     </div>
//                     <div className={styles.buttonContainer}>
//                         <button
//                             className={styles.loginButton}
//                             onClick={signIn}>
//                             ZALOGUJ
//                         </button>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     )
// }

// export default SignIn;
